import { Injectable } from '@angular/core';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

@Injectable({
  providedIn: 'root',
})
export class MapClusterService {
  createClusterer(map: google.maps.Map): MarkerClusterer {
    return new MarkerClusterer({
      map: map,
      markers: [],
      algorithm: new GridAlgorithm({
        gridSize: 60,
        maxZoom: 15,
      }),
      renderer: {
        render: ({ count, position }) => {
          const color = this.getClusterColor(count);
          const size = this.getClusterSize(count);

          return new google.maps.Marker({
            position,
            icon: {
              url: this.createClusterSVG(size, color, count),
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(size / 2, size / 2),
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      },
    });
  }

  /**
   * Returns the color based on the number of markers
   */
  private getClusterColor(count: number): string {
    if (count > 100) return '#e63600';
    if (count > 10) return '#0041ab';
    return '#1c73ff';
  }

  /**
   * Returns the size based on the number of markers
   */
  private getClusterSize(count: number): number {
    if (count > 100) return 64;
    if (count > 10) return 56;
    return 48;
  }

  /**
   * Creates the SVG for the cluster
   */
  private createClusterSVG(size: number, color: string, count: number): string {
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2"/>
        <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="14" font-weight="bold">${count}</text>
      </svg>
    `;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  /**
   * Adds markers to the clusterer
   */
  addMarkers(clusterer: MarkerClusterer | null, markers: google.maps.Marker[]): void {
    if (clusterer && markers.length > 0) {
      clusterer.clearMarkers();
      clusterer.addMarkers(markers);
    }
  }

  /**
   * Clears the clusterer
   */
  clearMarkers(clusterer: MarkerClusterer | null): void {
    if (clusterer) {
      clusterer.clearMarkers();
    }
  }

  /**
   * Cleans up and destroys the clusterer
   */
  destroy(clusterer: MarkerClusterer | null): MarkerClusterer | null {
    if (clusterer) {
      clusterer.clearMarkers();
      return null;
    }
    return null;
  }
}
